import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocketContext } from "../contexts/WebSocketProvider";

export default function usePaymentTransactionsByStation(stationId, range = null) {
    const [transactions, setTransactions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const { send, isConnected, isOpen, connect, registerHandler } = useWebSocketContext();
    const mountedRef = useRef(true);
    const retryTimeoutRef = useRef(null);

    // Default 30-day range
    const defaultRange = () => {
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - 29);
        const fmt = (d) => d.toISOString().split("T")[0];
        return { start_date: fmt(start), end_date: fmt(end) };
    };

    const normalizeRange = (r) => {
        if (!r) return defaultRange();
        if (r.start_date && r.end_date) return r;
        if (r.fromDate && r.toDate) return { start_date: r.fromDate, end_date: r.toDate };
        return defaultRange();
    };

    // Get role_id from localStorage
    const roleId = localStorage.getItem("role_id");

    const fetchTransactions = useCallback(async (attempt = 0) => {
        if (!mountedRef.current) return;

        setIsLoading(true);
        setError('');
        setMessage('');

        try {
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
            }

            if (!isConnected) {
                await connect();

                await new Promise((resolve, reject) => {
                    const start = Date.now();
                    const checkInterval = setInterval(() => {
                        if (!mountedRef.current) {
                            clearInterval(checkInterval);
                            reject(new Error('Component unmounted'));
                        } else if (isOpen()) {
                            clearInterval(checkInterval);
                            resolve();
                        } else if (Date.now() - start > 5000) {
                            clearInterval(checkInterval);
                            reject(new Error('Connection timeout'));
                        }
                    }, 100);
                });
            }

            const appliedRange = normalizeRange(range);

            const response = await send('cms_get_payment_transactions_by_station_id', {
                station_id: stationId,
                role_id: roleId,
                from_date: appliedRange.start_date,
                to_date: appliedRange.end_date
            });
            console.log('Payment Transactions by Station response:', response);

            if (!response || response.type !== 'cms_get_payment_transactions_by_station_id_response') {
                throw new Error('Invalid response from server');
            }

            if (!Array.isArray(response.data)) {
                throw new Error('No data available. Try refreshing the page');
            }

            setTransactions(response.data);
            setMessage(response.message || 'Transactions fetched successfully');
        } catch (err) {
            setError(err.message);

            if (mountedRef.current) {
                const delay = Math.min(1000 * Math.pow(2, attempt), 30000);
                retryTimeoutRef.current = setTimeout(() => {
                    fetchTransactions(attempt + 1);
                }, delay);
            }
        } finally {
            if (mountedRef.current) setIsLoading(false);
        }
    }, [isConnected, isOpen, connect, send, stationId, roleId, range]);

    useEffect(() => {
        mountedRef.current = true;
        fetchTransactions();

        const unregister = registerHandler('payment_transactions_update', (msg) => {
            if (Array.isArray(msg.data) && mountedRef.current) {
                setTransactions(msg.data);
            }
        });

        return () => {
            mountedRef.current = false;
            if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
            unregister();
        };
    }, [fetchTransactions, registerHandler, range]);

    return { transactions, isLoading, error, message, refetch: () => fetchTransactions(0) };
}
